import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import "../styles/Customer.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserPlus,
    faUser,
    faPhone,
    faEnvelope,
    faMapMarkerAlt,
    faBuilding,
    faGlobe,
    faUserTag,
    faStickyNote,
    faSave,
    faTimes,
    faSpinner,
    faCheckCircle,
    faExclamationTriangle,
    faStore,
    faExchangeAlt
} from '@fortawesome/free-solid-svg-icons';

// Create translator instance
const createSupplierTranslator = () => {
    let cache = new Map();
    let preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Supplier Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        // Form specific texts
        addSupplier: 'Add Supplier',
        fillDetails: 'Fill in the supplier details below',
        supplierName: 'Supplier Name',
        phoneNumber: 'Phone Number',
        email: 'Email Address',
        address: 'Address',
        company: 'Company',
        website: 'Website',
        contactPerson: 'Contact Person',
        notes: 'Notes',
        addSupplierButton: 'Save Supplier',
        clearButton: 'Clear',
        adding: 'Adding...',
        supplierAdded: 'Supplier saved successfully!',
        failedAdd: 'Failed to save supplier',
        networkError: 'Network error occurred',
        invalidPhone: 'Invalid phone number',
        invalidEmail: 'Invalid email address',

        // Validation messages
        nameRequired: 'Supplier name is required',
        nameTooShort: 'Name must be at least 2 characters',
        phoneInvalid: 'Please enter a valid phone number',
        emailInvalid: 'Please enter a valid email address',
        websiteInvalid: 'Please enter a valid website URL',

        // Success/Error states
        success: 'Success',
        error: 'Error',
        warning: 'Warning',

        // Shop related
        selectShop: 'Select a shop first',
        currentShop: 'Current Shop',
        willAddToShop: 'Supplier will be added to this shop',
        noShopSelected: 'No shop selected. Please switch to a shop first.',
        switchShop: 'Switch Shop',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        noShopPermission: 'You do not have permission to add suppliers to this shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        active: 'Active',
        current: 'Current',

        // Optional labels
        optional: ' Optional'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const supplierTranslator = createSupplierTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content shop-selector-modal">
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                        {shop.shop_role && (
                                            <div className="shop-item-role">Role: {shop.shop_role}</div>
                                        )}
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const AddSupplier = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [formData, setFormData] = useState({
        supplier_name: '',
        phone: '',
        email: '',
        address: '',
        company: '',
        website: '',
        contact_person: '',
        notes: '',
        themeColor: localStorage.getItem('ourzest_theme') || '#6366f1'
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Multiple refs for submission control
    const isSubmittingRef = useRef(false);
    const submissionLockRef = useRef(false);
    const lastSubmissionRef = useRef(null);
    const toastTimerRef = useRef(null);
    const formRef = useRef(null);

    // Function to show toast
    const showToast = (type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    };

    // Clean up timers on component unmount
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
            // Reset all submission flags
            isSubmittingRef.current = false;
            submissionLockRef.current = false;
        };
    }, []);

    // Debounced submit function
    const debouncedSubmit = useCallback(async (submitData) => {
        // Check if we're already submitting
        if (isSubmittingRef.current || submissionLockRef.current) {
            console.log('🚫 Submission blocked - already in progress');
            return;
        }

        // Check if this is the same as last submission (within 3 seconds)
        const now = Date.now();
        if (lastSubmissionRef.current) {
            const timeDiff = now - lastSubmissionRef.current.time;
            const dataMatch = JSON.stringify(submitData) === lastSubmissionRef.current.data;

            if (timeDiff < 3000 && dataMatch) {
                console.log('🚫 Duplicate submission prevented (client-side)');
                showToast('warning', 'Please wait before submitting again');
                return;
            }
        }

        // Set submission flags
        isSubmittingRef.current = true;
        submissionLockRef.current = true;
        setLoading(true);

        // Store this submission
        lastSubmissionRef.current = {
            time: now,
            data: JSON.stringify(submitData)
        };

        try {
            const currentUsername = user?.username || 'Unknown';

            if (!currentUsername || currentUsername === 'Unknown') {
                throw new Error("User not authenticated");
            }

            if (!currentShop) {
                throw new Error("No shop selected");
            }

            // Prepare data with unique submission ID
            const supplierData = {
                action: 'add',
                supplier_name: submitData.supplier_name,
                phone: submitData.phone,
                email: submitData.email,
                address: submitData.address,
                company: submitData.company,
                website: submitData.website,
                contact_person: submitData.contact_person,
                notes: submitData.notes,
                username: currentUsername,
                shop_id: currentShop.shop_id,
                submission_timestamp: now
            };

            console.log('📤 Submitting supplier data:', supplierData);

            const response = await fetch('https://ourzestapp.online/oz_backend/suppliers.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(supplierData)
            });

            const data = await response.json();
            console.log('📥 Supplier API Response:', data);

            if (data.status === 'success') {
                // Show success message with supplier login info if created
                let successMessage = translations.supplierAdded || 'Supplier saved successfully!';
                if (data.supplier_login && data.supplier_login.created) {
                    successMessage += ` Supplier login: ${data.supplier_login.username} (Password: Supplier@123)`;
                }
                showToast('success', successMessage);

                // Reset form
                setFormData({
                    supplier_name: '',
                    phone: '',
                    email: '',
                    address: '',
                    company: '',
                    website: '',
                    contact_person: '',
                    notes: '',
                    themeColor: formData.themeColor
                });
                setErrors({});

                // Clear form after successful submission
                if (formRef.current) {
                    formRef.current.reset();
                }
            } else {
                // Handle duplicate submission error from server
                if (data.duplicate_prevention || data.duplicate_database) {
                    showToast('warning', data.message || 'Duplicate submission detected');
                } else {
                    showToast('error', data.message || translations.failedAdd || 'Failed to save supplier');
                }
            }
        } catch (error) {
            console.error('❌ Error adding supplier:', error);
            showToast('error', error.message || translations.networkError || 'Network error occurred');
        } finally {
            // Reset submission flags with a delay
            setTimeout(() => {
                isSubmittingRef.current = false;
                submissionLockRef.current = false;
                setLoading(false);
            }, 1000);
        }
    }, [currentShop, user, translations, showToast]);

    // Handle form submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        console.log('🔔 Form submit triggered at:', new Date().toISOString());

        // Check if shop is selected
        if (!currentShop) {
            showToast('error', translations.selectShop || 'Please select a shop first');
            setShowShopSelector(true);
            return;
        }

        if (!validateForm()) {
            return;
        }

        // Call debounced submit
        debouncedSubmit(formData);
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.supplier_name.trim()) {
            newErrors.supplier_name = translations.nameRequired || 'Supplier name is required';
        } else if (formData.supplier_name.trim().length < 2) {
            newErrors.supplier_name = translations.nameTooShort || 'Name must be at least 2 characters';
        }

        if (formData.phone && !/^[\d\s\-\+\(\)]{10,15}$/.test(formData.phone)) {
            newErrors.phone = translations.phoneInvalid || 'Please enter a valid phone number';
        }

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = translations.emailInvalid || 'Please enter a valid email address';
        }

        if (formData.website && !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(formData.website)) {
            newErrors.website = translations.websiteInvalid || 'Please enter a valid website URL';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleClear = () => {
        setFormData({
            supplier_name: '',
            phone: '',
            email: '',
            address: '',
            company: '',
            website: '',
            contact_person: '',
            notes: '',
            themeColor: formData.themeColor
        });
        setErrors({});

        // Reset submission refs
        lastSubmissionRef.current = null;
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(
                shop.shop_id,
                shop.shop_name,
                shop.shop_code
            );

            if (success) {
                showToast('success', translations.shopSwitched || 'Shop switched successfully');
            } else {
                showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
        }
    };

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#6366f1';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'customer-primary-color': color,
                    'customer-primary-dark': adjust(-40),
                    'customer-primary-light': adjust(40),
                    'customer-primary-ultralight': adjust(20),
                    'customer-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'customer-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });
            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(formData.themeColor);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#6366f1';
            if (theme !== formData.themeColor) {
                setFormData(prev => ({ ...prev, themeColor: theme }));
                applyTheme(theme);
            }
        };

        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [formData.themeColor]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await supplierTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Listen for language changes
    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
            }
        };

        handleLanguageChange();
        window.addEventListener('storage', handleLanguageChange);
        const interval = setInterval(handleLanguageChange, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // Apply RTL direction
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
            document.documentElement.style.direction = 'rtl';
            const forms = document.querySelectorAll('.customer-form-independent, .modern-form, .form-group-modern, .input-container');
            forms.forEach(form => {
                form.style.direction = 'rtl';
                form.style.textAlign = 'right';
            });
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
            const forms = document.querySelectorAll('.customer-form-independent, .modern-form, .form-group-modern, .input-container');
            forms.forEach(form => {
                form.style.direction = 'ltr';
                form.style.textAlign = 'left';
            });
        }

        return () => {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
        };
    }, [currentLanguage]);

    // Load available shops
    useEffect(() => {
        const loadAvailableShops = async () => {
            if (!user) return;

            setLoadingShops(true);
            try {
                const shopsData = await getShopsForSwitch();
                setAvailableShops(shopsData.shops || []);
            } catch (error) {
                console.error('Error loading shops:', error);
                setAvailableShops([]);
            } finally {
                setLoadingShops(false);
            }
        };

        loadAvailableShops();
    }, [user, getShopsForSwitch]);

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button
                        className="btn-modern btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faExchangeAlt} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }

        // return (
        //     <div className="shop-indicator-modern active">
        //         <div className="shop-badge">
        //             <FontAwesomeIcon icon={faStore} />
        //             <div className="shop-details">
        //                 <span className="shop-name">{currentShop.shop_name}</span>
        //                 <span className="shop-code">({currentShop.shop_code || currentShop.shop_id})</span>
        //             </div>
        //         </div>
        //         <div className="shop-info-note">
        //             {translations.willAddToShop || 'Supplier will be added to this shop'}
        //         </div>
        //         <button
        //             className="btn-modern btn-switch-shop"
        //             onClick={() => setShowShopSelector(true)}
        //         >
        //             <FontAwesomeIcon icon={faExchangeAlt} />
        //             <span>{translations.switchShop || 'Switch Shop'}</span>
        //         </button>
        //     </div>
        // );
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`customer-form-independent ${isRTL ? 'rtl' : ''}`}>
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Shop Selector Modal */}
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Modern Form Header */}
            <div className="form-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faUserPlus} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.addSupplier || 'Save Supplier'}</h1>
                        <p>{translations.fillDetails || 'Fill in the supplier details below'}</p>
                    </div>
                </div>

                {/* Shop Indicator */}
                {renderShopIndicator()}
            </div>

            {/* Modern Form */}
            <form
                ref={formRef}
                onSubmit={handleSubmit}
                className="modern-form"
                onKeyDown={(e) => {
                    // Prevent Enter key from submitting multiple times
                    if (e.key === 'Enter' && (isSubmittingRef.current || submissionLockRef.current)) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }}
            >
                <div className="form-grid-modern">
                    {/* Supplier Name Field */}
                    <div className={`form-group-modern ${errors.supplier_name ? 'has-error' : ''}`}>
                        <div className="input-container">
                            <FontAwesomeIcon icon={faUser} className="input-icon" />
                            <input
                                type="text"
                                id="supplier_name"
                                name="supplier_name"
                                value={formData.supplier_name}
                                onChange={handleChange}
                                required
                                placeholder={translations.supplierName || 'Enter supplier name'}
                                disabled={loading || isSubmittingRef.current}
                                dir={isRTL ? 'rtl' : 'ltr'}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && (isSubmittingRef.current || submissionLockRef.current)) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                            <label htmlFor="supplier_name" className="input-label">
                                {translations.supplierName || 'Supplier Name'}
                                <span className="required">*</span>
                            </label>
                        </div>
                        {errors.supplier_name && (
                            <div className="error-message-modern">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                {errors.supplier_name}
                            </div>
                        )}
                    </div>

                    {/* Phone Number Field */}
                    <div className={`form-group-modern ${errors.phone ? 'has-error' : ''}`}>
                        <div className="input-container">
                            <FontAwesomeIcon icon={faPhone} className="input-icon" />
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder={translations.phoneNumber || 'Enter phone number'}
                                disabled={loading || isSubmittingRef.current}
                                dir="ltr"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && (isSubmittingRef.current || submissionLockRef.current)) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                            <label htmlFor="phone" className="input-label">
                                {translations.phoneNumber || 'Phone Number'}
                                <span className="optional">{translations.optional || ' Optional'}</span>
                            </label>
                        </div>
                        {errors.phone && (
                            <div className="error-message-modern">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                {errors.phone}
                            </div>
                        )}
                    </div>

                    {/* Email Field */}
                    {/* <div className={`form-group-modern ${errors.email ? 'has-error' : ''}`}>
                        <div className="input-container">
                            <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder={translations.email || 'Enter email address'}
                                disabled={loading || isSubmittingRef.current}
                                dir="ltr"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && (isSubmittingRef.current || submissionLockRef.current)) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                            <label htmlFor="email" className="input-label">
                                {translations.email || 'Email Address'}
                                <span className="optional">{translations.optional || ' Optional'}</span>
                            </label>
                        </div>
                        {errors.email && (
                            <div className="error-message-modern">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                {errors.email}
                            </div>
                        )}
                    </div> */}

                    {/* Company Field */}
                    <div className="form-group-modern">
                        <div className="input-container">
                            <FontAwesomeIcon icon={faBuilding} className="input-icon" />
                            <input
                                type="text"
                                id="company"
                                name="company"
                                value={formData.company}
                                onChange={handleChange}
                                placeholder={translations.company || 'Enter company name'}
                                disabled={loading || isSubmittingRef.current}
                                dir={isRTL ? 'rtl' : 'ltr'}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && (isSubmittingRef.current || submissionLockRef.current)) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                            <label htmlFor="company" className="input-label">
                                {translations.company || 'Company'}
                                <span className="optional">{translations.optional || ' Optional'}</span>
                            </label>
                        </div>
                    </div>

                    {/* Notes Field (Full Width) */}
                    {/* <div className="form-group-modern">
                        <div className="input-container">
                            <FontAwesomeIcon icon={faStickyNote} className="input-icon" />
                            <input
                            type='text'
                                id="notes"
                                name="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                rows="3"
                                placeholder={translations.notes || 'Enter any additional notes'}
                                disabled={loading || isSubmittingRef.current}
                                dir={isRTL ? 'rtl' : 'ltr'}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && (isSubmittingRef.current || submissionLockRef.current)) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                            <label htmlFor="notes" className="input-label">
                                {translations.notes || 'Notes'}
                                <span className="optional">{translations.optional || 'Optional'}</span>
                            </label>
                        </div>
                    </div> */}


                    {/* Website Field */}
                    {/* <div className="form-group-modern">
                        <div className="input-container">
                            <FontAwesomeIcon icon={faGlobe} className="input-icon" />
                            <input
                                type="url"
                                id="website"
                                name="website"
                                value={formData.website}
                                onChange={handleChange}
                                placeholder={translations.website || 'Enter website URL'}
                                disabled={loading || isSubmittingRef.current}
                                dir="ltr"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && (isSubmittingRef.current || submissionLockRef.current)) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                            <label htmlFor="website" className="input-label">
                                {translations.website || 'Website'}
                                <span className="optional">{translations.optional || ' Optional'}</span>
                            </label>
                        </div>
                    </div> */}

                    {/* Contact Person Field */}
                    {/* <div className="form-group-modern">
                        <div className="input-container">
                            <FontAwesomeIcon icon={faUserTag} className="input-icon" />
                            <input
                                type="text"
                                id="contact_person"
                                name="contact_person"
                                value={formData.contact_person}
                                onChange={handleChange}
                                placeholder={translations.contactPerson || 'Enter contact person name'}
                                disabled={loading || isSubmittingRef.current}
                                dir={isRTL ? 'rtl' : 'ltr'}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && (isSubmittingRef.current || submissionLockRef.current)) {
                                        e.preventDefault();
                                    }
                                }}
                            />
                            <label htmlFor="contact_person" className="input-label">
                                {translations.contactPerson || 'Contact Person'}
                                <span className="optional">{translations.optional || ' Optional'}</span>
                            </label>
                        </div>
                    </div> */}

                </div>

                {/* Address Field (Full Width) */}
                <div className="form-group-modern">
                    <div className="input-container">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="input-icon" />
                        <input
                        type='text'
                            id="address"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            rows="3"
                            placeholder={translations.address || 'Enter supplier address'}
                            disabled={loading || isSubmittingRef.current}
                            dir={isRTL ? 'rtl' : 'ltr'}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && (isSubmittingRef.current || submissionLockRef.current)) {
                                    e.preventDefault();
                                }
                            }}
                        />
                        <label htmlFor="address" className="input-label">
                            {translations.address || 'Address'}
                            <span className="optional">{translations.optional || ' Optional'}</span>
                        </label>
                    </div>
                </div>



                {/* Form Actions */}
                <div className="form-actions-modern">
                    <button
                        type="button"
                        className="btn-modern btn-secondary-modern"
                        onClick={handleClear}
                        disabled={loading || isSubmittingRef.current}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                        <span>{translations.clearButton || 'Clear'}</span>
                    </button>
                    <button
                        type="submit"
                        className="btn-modern btn-primary-modern"
                        disabled={loading || !currentShop || isSubmittingRef.current || submissionLockRef.current}
                        onMouseDown={(e) => {
                            // Prevent multiple clicks
                            if (isSubmittingRef.current || submissionLockRef.current) {
                                e.preventDefault();
                            }
                        }}
                        onClick={(e) => {
                            // Additional click prevention
                            if (isSubmittingRef.current || submissionLockRef.current) {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('⚠️ Button click prevented - submission in progress');
                            }
                        }}
                    >
                        {loading || isSubmittingRef.current ? (
                            <>
                                <div className="btn-spinner"></div>
                                <span>{translations.adding || 'Adding...'}</span>
                            </>
                        ) : (
                            <>
                                <span>{translations.addSupplierButton || 'Save Supplier'}</span>
                                <FontAwesomeIcon icon={faSave} />
                            </>
                        )}
                    </button>
                </div>

                {/* Submission Status Indicator */}
                {(isSubmittingRef.current || submissionLockRef.current) && (
                    <div className="submission-status">
                        <div className="submission-status-text">
                            <FontAwesomeIcon icon={faSpinner} spin />
                            <span>Processing your submission...</span>
                        </div>
                    </div>
                )}
            </form>

            {/* Loading Overlay */}
            {loading && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <div className="loading-spinner"></div>
                        <p>{translations.adding || 'Adding...'}</p>
                        <p className="loading-subtext">Please wait while we save supplier data</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddSupplier;